import { checkedTrackedItems } from "../../lib/tracked-items";
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import ExpenseForm from "../entry/ExpenseForm";
import type { DashboardData, Owner } from "../../types";
import type { ReceiptDraft } from "../../types/receipt";
import { money } from "../../lib/finance";
import { appendReceiptFiles, receiptImageGroups, type ReceiptAttachment } from "../../lib/image-data";
import LoadingLabel from "../../components/LoadingLabel";
import ReceiptCapture from "./ReceiptCapture";
import ReceiptReviewItems from "./ReceiptReviewItems";
import { ReceiptTracking } from "./ReceiptTracking";
import { receiptKey, restoreReceipt, type ReceiptState } from "../../lib/receipt-draft";
import { putOfflineBlob } from "../../lib/offline-blobs";
import { enqueuePendingReceipt } from "../../lib/pending-receipts";
import { shouldDeferCreate } from "../../lib/offline-associated";
export default function ReceiptFlow({
  data,
  profile,
  onSaved,
  onPendingChange,
}: {
  data: DashboardData;
  profile: Owner;
  onSaved: (accountIds?: string[]) => void;
  onPendingChange: (value: boolean) => void;
}) {
  const [state, setState] = useState<ReceiptState | null>(() =>
      restoreReceipt(profile),
    ),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [attachments, setAttachments] = useState<ReceiptAttachment[]>([]);
  const scanning = useRef(false),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    onPendingChange(busy || !!state?.pending);
    return () => onPendingChange(false);
  }, [busy, state?.pending, onPendingChange]);
  function update(next: ReceiptState) {
    localStorage.setItem(receiptKey(profile), JSON.stringify(next));
    setState(next);
  }
  function editState(patch: Partial<ReceiptState>) {
    if (!state) return;
    try {
      update({ ...state, ...patch, updated: Date.now() });
    } catch {
      setError("Enable browser storage to keep your receipt draft.");
    }
  }
  function editDraft(patch: Partial<ReceiptDraft>) {
    if (state) editState({ draft: { ...state.draft, ...patch } });
  }
  async function queueOfflineScan(selected: ReceiptAttachment[]) {
    const id = crypto.randomUUID();
    for (const item of selected) await putOfflineBlob(item.id, item.file);
    enqueuePendingReceipt({
      kind: "scan",
      id,
      owner: profile,
      attachments: selected.map((item) => ({
        id: item.id,
        sameAsPrevious: item.sameAsPrevious,
        name: item.file.name,
        type: item.file.type,
      })),
    });
    setAttachments([]);
    onSaved();
  }

  async function scan(selected = attachments) {
    if (!selected.length) return;
    if (scanning.current) return;
    scanning.current = true;
    setBusy(true);
    setError("");
    try {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        await queueOfflineScan(selected);
        return;
      }
      const { receipts } = await api<{ receipts: ReceiptDraft[] }>("receipts/scan", await receiptImageGroups(selected));
      if (mounted.current) {
        const [draft, ...remainingDrafts] = receipts;
        update({ draft, remainingDrafts, receiptNumber: 1, receiptTotal: receipts.length, step: "details", pending: false, updated: Date.now() });
        setAttachments([]);
      }
    } catch (e) {
      if (shouldDeferCreate(e)) {
        try {
          await queueOfflineScan(selected);
          return;
        } catch (persistError) {
          if (mounted.current)
            setError(persistError instanceof Error ? persistError.message : "Could not keep the receipt photos on this device.");
          return;
        }
      }
      if (mounted.current)
        setError(
          e instanceof Error ? e.message : "Could not read the receipt.",
        );
    } finally {
      scanning.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  function finishReceipt() {
    if (!state) return;
    const [next, ...remainingDrafts] = state.remainingDrafts || [];
    const savedAccountIds = [...new Set([...(state.savedAccountIds || []), state.draft.expense.accountId])];
    if (next) {
      update({ draft: next, remainingDrafts, receiptNumber: (state.receiptNumber || 1) + 1, receiptTotal: state.receiptTotal || 1, savedAccountIds, step: "details", pending: false, updated: Date.now(), trackingItemIds: [] });
    } else {
      localStorage.removeItem(receiptKey(profile));
      setState(null);
      onSaved(savedAccountIds);
    }
  }
  async function save(draft = state?.draft) {
    if (!state || !draft || busy || scanning.current) return;
    scanning.current = true;
    setBusy(true);
    setError("");
    let expenseVerified = false;
    let attachmentFailed = false;
    try {
      const selected = draft.items.filter((item) =>
        state.trackingItemIds?.includes(item.id),
      );
      checkedTrackedItems(selected.map(item => ({ ...item, notes: "" })));
      const pending = { ...state, draft, pending: true };
      update(pending);
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        enqueuePendingReceipt({
          kind: "save",
          id: draft.id,
          owner: profile,
          draft,
          trackingItemIds: selected.map((item) => item.id),
        });
        if (mounted.current) finishReceipt();
        return;
      }
      const result = await api<{ verified: boolean }>("receipts/save", draft);
      if (!result.verified)
        throw new Error("Verification is pending. Check this save again.");
      expenseVerified = true;
      for (const image of draft.pendingImages || []) {
        try {
          await api("receipts/image", { receiptId: draft.id, ...image });
        } catch {
          attachmentFailed = true;
        }
      }
      if (attachmentFailed)
        throw new Error("Receipt saved, but its photo still needs attaching.");
      for (const item of selected) {
        const price = await api<{ verified: boolean }>("prices", {
          receiptId: draft.id,
          item: { ...item, group: item.group || item.name },
        });
        if (!price.verified)
          throw new Error(
            "Price verification is pending. Retry to finish saving the selected items.",
          );
      }
      if (mounted.current) finishReceipt();
    } catch (e) {
      if (!expenseVerified && shouldDeferCreate(e) && state && draft) {
        try {
          enqueuePendingReceipt({
            kind: "save",
            id: draft.id,
            owner: profile,
            draft,
            trackingItemIds: (state.trackingItemIds || []).filter((id) =>
              draft.items.some((item) => item.id === id),
            ),
          });
          if (mounted.current) finishReceipt();
          return;
        } catch { /* Fall through to the normal error path. */ }
      }
      if (mounted.current) {
        if (!expenseVerified && e instanceof ApiError && !e.uncertain)
          update({ ...state, draft, pending: false });
        setError(
          attachmentFailed
            ? "Expense and receipt saved. The photo still needs attaching. Retry to finish; the expense will not be added again."
            : expenseVerified
            ? "Expense saved. Some selected prices still need verification. Retry to finish; the expense will not be added again."
            : e instanceof Error
              ? e.message
              : "Could not confirm the save. Check save again.",
        );
      }
    } finally {
      scanning.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <div
      className="receipt-ui"
      onPaste={(e) => {
        if (state || busy) return;
        const images = Array.from(e.clipboardData.items).filter((item) => item.kind === "file" && item.type.startsWith("image/")).map((item) => item.getAsFile()).filter((file): file is File => !!file);
        if (!images.length) return;
        e.preventDefault();
        setAttachments((current) => appendReceiptFiles(current, images));
      }}
    >
      {!state ? (
        <ReceiptCapture attachments={attachments} setAttachments={setAttachments} busy={busy} onScan={() => void scan()} setError={setError} />
      ) : (
        <>
          <div className="receipt-stage">
            <span>
              {state.step === "tracking"
                ? "Expense saved"
                : `${state.receiptTotal && state.receiptTotal > 1 ? `Receipt ${state.receiptNumber} of ${state.receiptTotal} · ` : ""}${state.draft.items.length} items · Review & edit`}
            </span>
            {!state.pending && (
              <button
                disabled={busy}
                onClick={() => {
                  localStorage.removeItem(receiptKey(profile));
                  setState(null);
                  setError("");
                }}
              >
                {state.step === "tracking" ? "Scan another" : "Discard draft"}
              </button>
            )}
          </div>
          {state.draft.warning && state.step !== "tracking" && (
            <details className="receipt-warning">
              <summary>Scan notes · check unclear details</summary>
              <p>{state.draft.warning}</p>
            </details>
          )}
          {state.step !== "tracking" ? (
            <>
              <ReceiptReviewItems state={state} busy={busy} editState={editState} editDraft={editDraft} />
              <p className="muted">
                Items total:{" "}
                {money(
                  state.draft.items.reduce(
                    (sum, i) => sum + Number(i.total || 0),
                    0,
                  ),
                  state.draft.currency,
                )}
                . Check any difference against discounts, tax or fees.
              </p>
              {state.pending ? (
                <button
                  className="primary-button"
                  disabled={busy}
                  onClick={() => void save()}
                >
                  {busy
                    ? <LoadingLabel>Saving</LoadingLabel>
                    : "Retry saving expense & selected prices"}
                </button>
              ) : (
                <div className="receipt-expense-details">
                  <h3>Expense details</h3>
                  <label>
                    Receipt currency
                    <select
                      aria-label="Receipt currency"
                      value={state.draft.currency}
                      onChange={(e) => editDraft({ currency: e.target.value })}
                    >
                      <option value="">Choose currency</option>
                      {[...new Set((data.accounts || []).map((a) => a.currency))].map(
                        (c) => (
                          <option key={c}>{c}</option>
                        ),
                      )}
                    </select>
                  </label>
                  <ExpenseForm
                    receipt={state.draft}
                    onReceiptChange={draft => editDraft(draft)}
                    data={data}
                    profile={profile}
                    initialInput={state.draft.expense}
                    autoFocusAmount={false}
                    reviewLabel="Save expense"
                    onSaved={() => {}}
                    onDraftChange={(expense) => editDraft({ expense })}
                    onReview={(expense) => {
                      if (
                        (data.accounts || []).find((a) => a.id === expense.accountId)
                          ?.currency !== state.draft.currency
                      ) {
                        setError("Choose an account in the receipt currency.");
                        return;
                      }
                      void save({ ...state.draft, expense });
                    }}
                  />
                </div>
              )}
              <p className="smart-caption">
                Saves the receipt expense and only the items selected for price
                tracking.
              </p>
            </>
          ) : (
            <ReceiptTracking
              draft={{
                ...state.draft,
                items: state.trackingItemIds?.length
                  ? state.draft.items.filter((item) =>
                      state.trackingItemIds?.includes(item.id),
                    )
                  : [],
              }}
              initialSelectedIds={state.trackingItemIds || []}
              onDone={finishReceipt}
            />
          )}
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
